import React, { useEffect, useRef, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { HTTP_STATUS } from '../../utils/httpStatusCodes';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCb6XBv9tnLgqhPfWHBlzokjXagwfX3vtk';

const AddSelectCustomerModal = ({ isOpen, onClose, orderNumbers,orderType, onCustomerUpdated }) => {
  const [customer, setCustomer] = useState({
    first_name: '',
    last_name: '',
    mail: '',
    number: '',
    subscribed: false,
    address_line_1: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
  });

  const [suggestions, setSuggestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [searchCustomer, setSearchCustomer] = useState('');
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const loadGoogleMapsScript = () =>
    new Promise((resolve, reject) => {
      if (window.google?.maps?.places) return resolve();

      const existing = document.getElementById('googleMapsScript');
      if (existing) {
        existing.onload = resolve;
        return;
      }

      const script = document.createElement('script');
      script.id = 'googleMapsScript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });

    const normalizeErrors = (res) => {
      const apiErrors = res?.data?.errors || res?.errors || {};
    
      const updatedErrors = {};
    
      Object.entries(apiErrors).forEach(([field, msgs]) => {
        const messages = Array.isArray(msgs) ? msgs : [msgs];
    
        updatedErrors[field] = messages.map((msg) => {
          const isAddressField = [
            'address_line_1',
            'postal_code',
            'city',
            'state',
            'country',
          ].includes(field);
    
          if (isAddressField && orderType !== 2) {
            return `${msg} (Since this not a delivery order, you can remove address details)`;
          }
    
          return msg;
        });
      });
    
      return updatedErrors;
    };
  const fetchSuggestions = async (value) => {
    if (!value || value.length < 2) {
      setSuggestions([]);
      return;
    }

    const url = `${apiUrls.CUSTOMER_AUTOCOMPLETE}?field=first_name&term=${value}`;
    const res = await callAPI(url, {}, 'GET');

    if (res?.data?.status === 1) {
      setSuggestions(res.data.data || []);
    }
  };

  const handleSuggestionSelect = (item) => {
    const c = item.customerObject || {};

    setCustomer((prev) => ({
      ...prev,
      first_name: c.firstName || '',
      last_name: c.lastName || '',
      mail: c.email || '',
      number: c.contactNumber || '',
      subscribed: !!c.subscribed,
      address_line_1: c.address_line_1 || '',
      city: c.city || '',
      state: c.state || '',
      country: c.country || '',
      postal_code: c.postal_code || '',
    }));

    setSearchCustomer(c.id)
    setSuggestions([]);
  };

  

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const initAutocomplete = async () => {
      await loadGoogleMapsScript();
      if (!addressInputRef.current || !isMounted) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          componentRestrictions: { country: 'au' },
          fields: ['address_components', 'formatted_address'],
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place) return;

        setCustomer((prev) => {
          const updated = {
            ...prev,
            address_line_1: place.formatted_address || '',
          };

          place.address_components?.forEach((comp) => {
            if (comp.types.includes('locality')) updated.city = comp.long_name;
            if (comp.types.includes('administrative_area_level_1')) updated.state = comp.long_name;
            if (comp.types.includes('country')) updated.country = comp.long_name;
            if (comp.types.includes('postal_code')) updated.postal_code = comp.long_name;
          });

          return updated;
        });
      });
    };

    initAutocomplete();

    return () => {
      isMounted = false;
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isOpen]);



  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `.pac-container { z-index: 9999 !important; }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'first_name') fetchSuggestions(value);

    if (['number', 'first_name', 'last_name', 'mail'].includes(name)) {
      setSearchCustomer('');
    }
    setCustomer((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };


  const handleSave = async () => {
    setErrors({});
  
    const payload = {
      ...customer,
      name: `${customer.first_name} ${customer.last_name}`,
      mobile: customer.number,
      order_id: orderNumbers,
    };
  
    if (orderType === 2 && !customer.number) {
      setErrors({ number: 'Phone number is required for delivery' });
      return;
    }

    if (searchCustomer) {
      await updateCustomerInOrder(searchCustomer);
      return;
    }

    if (!customer.last_name && !customer.number && orderType !== 2) {
  
      if (!customer.first_name) {
        setErrors({ first_name: 'First name is required.' });
        return;
      }
  
      await saveCustomerName(payload);
      return;
    }
  
    const res = await callAPI(apiUrls.SAVE_CUSTOMER, {}, 'POST', payload);
  
    const apiErrors = normalizeErrors(res);

    if (Object.keys(apiErrors).length > 0) {
      setErrors(apiErrors);
      return;
    }
  
    if (res.data?.status === 409) {
      setErrors({ number: res.data.message });
      return;
    }
  
    if (res.data?.status === 400) {
      setErrors(res?.data?.errors || {});
      return;
    }
  
    if (res.data?.status === 1) {
      await updateCustomerInOrder(res.data.customer);
    }
  };

  const saveCustomerName = async (payload) => {
    const res = await callAPI(apiUrls.SAVE_CUSTOMER_NAME, {}, 'POST', payload);
    const apiErrors = normalizeErrors(res);

    if (Object.keys(apiErrors).length > 0) {
      setErrors(apiErrors);
      return;
    }
  
    if (res.data?.status === 1) {
      onCustomerUpdated?.();
      onClose();
    }
  };

  const updateCustomerInOrder = async (customerId) => {
    const res = await callAPI(apiUrls.UPDATE_ORDER_CUSTOMER, {}, 'POST', {
      order_numbers: orderNumbers,
      customer: customerId,
    });

    if (res.status === HTTP_STATUS.OK) {
      onCustomerUpdated?.();
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
  
    setCustomer({
      first_name: '',
      last_name: '',
      mail: '',
      number: '',
      subscribed: false,
      address_line_1: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
    });
  
    setErrors({});
    setSearchCustomer('');
    setSuggestions([]);
  
  }, [isOpen]);
  

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="addSummaryCustomerModal">
      <div className="modal-header">
        <h5 className="modal-title">Add or Select Customer</h5>
        <button className="btn-close" onClick={onClose}></button>
      </div>

      <div className="modal-body formStyle">
      {Object.keys(errors || {}).length > 0 && (
        <div className="alert alert-danger py-2">
          {Object.entries(errors).map(([field, msgs]) =>
            msgs.map((msg, i) => (
              <div key={`${field}-${i}`}>{msg}</div>
            ))
          )}
        </div>
      )}
            <div className="row g-3">

          <div className="col-md-6 position-relative">
            <input
              name="first_name"
              value={customer.first_name}
              onChange={handleChange}
              className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
              placeholder="First Name"
              autoComplete="off"
            />
            <div className="invalid-feedback">{errors.first_name}</div>
            {suggestions.length > 0 && (
              <ul className="autocomplete-list list-group position-absolute w-100 z-3">
                {suggestions.map((s, i) => (
                  <li
                    key={i}
                    className="list-group-item list-group-item-action"
                    onClick={() => handleSuggestionSelect(s)}
                  >
                    {s.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="col-md-6">
            <input
              name="last_name"
              value={customer.last_name}
              onChange={handleChange}
              className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
              placeholder="Last Name"
            />
            <div className="invalid-feedback">{errors.last_name}</div>
          </div>

          <div className="col-md-6">
            <input
              name="mail"
              value={customer.mail}
              onChange={handleChange}
              className={`form-control ${errors.mail ? 'is-invalid' : ''}`}
              placeholder="Email Address"
            />
            <div className="invalid-feedback">{errors.mail}</div>
          </div>

          <div className="col-md-6">
            <input
              name="number"
              value={customer.number}
              onChange={handleChange}
              className={`form-control ${errors.number ? 'is-invalid' : ''}`}
              placeholder="Phone Number"
            />
            <div className="invalid-feedback">{errors.number}</div>
          </div>

          <div className="col-md-12">
            <input
              ref={addressInputRef}
              name="address_line_1"
              value={customer.address_line_1}
              onChange={handleChange}
              className="form-control"
              placeholder="Start typing your address"
              autoComplete="off"
            />
          </div>

          <div className="col-md-12 form-check">
            <input
              type="checkbox"
              name="subscribed"
              checked={customer.subscribed}
              onChange={handleChange}
              className="form-check-input"
              id="subscribed"
            />
            <label className="form-check-label" htmlFor="subscribed">
              Receive discount vouchers & special promotions
            </label>
          </div>

        </div>
      </div>

      <div className="modal-footer">
        <button className="btn btnLight" onClick={onClose}>Close</button>
        <button className="btn btnPrimary" onClick={handleSave}>Save Changes</button>
      </div>
    </CommonModal>
  );
};

export default AddSelectCustomerModal;
